<script setup lang="ts">
/** Administration → MCP clients: apps registered with smake-mcp's MCP sign-in; approve the waiting ones, block bad ones. */
import { Ban, BadgeCheck, Blocks } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, h, ref } from "vue";
import { toast } from "vue-sonner";
import { approveMcpClient, blockMcpClient, clientName, useMcpClients, type ClientStatus, type McpClient } from "@/api/oauth";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { confirm } from "@/composables/useConfirm";
import { oauth } from "@/copy/oauth";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import type { Column } from "@/lib/table";

definePage({ meta: { title: "MCP clients", requires: { adminArea: "access" } } });

const c = oauth.clients;
const queryClient = useQueryClient();
const status = ref<ClientStatus | undefined>(undefined);
const clients = useMcpClients(status);
const selected = ref<McpClient | null>(null);
const open = ref(false);
const busy = ref(false);

const STATUS_VARIANT: Record<ClientStatus, "success" | "warning" | "danger"> = { active: "success", pending: "warning", blocked: "danger" };
const statusBadge = (s: ClientStatus) => h(Badge, { variant: STATUS_VARIANT[s] }, () => oauth.status[s]);
const filterOptions = [
  { value: "pending" as const, label: oauth.filter.pending },
  { value: "active" as const, label: oauth.filter.active },
  { value: "blocked" as const, label: oauth.filter.blocked },
];

function show(client: McpClient) {
  selected.value = client;
  open.value = true;
}

async function decide(client: McpClient, next: "active" | "blocked") {
  const name = clientName(client);
  const answer =
    next === "active"
      ? await confirm({ title: `${client.status === "blocked" ? c.unblock : c.approve} ${name}?`, description: c.approveConfirm(name), confirmLabel: client.status === "blocked" ? c.unblock : c.approve, reason: { minLength: 0 } })
      : await confirm({ title: `${c.block} ${name}?`, description: c.blockConfirm(name), confirmLabel: c.block, destructive: true, reason: true });
  if (!answer) return;
  busy.value = true;
  try {
    if (next === "active") await approveMcpClient(client.uid, answer.reason || undefined);
    else await blockMcpClient(client.uid, answer.reason);
    toast.success(next === "active" ? c.approved : c.blocked);
    open.value = false;
    await queryClient.invalidateQueries({ queryKey: ["admin", "mcp-clients"] });
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    busy.value = false;
  }
}

const columns = computed<Column<McpClient>[]>(() => [
  {
    id: "app",
    header: c.columns.app,
    accessorFn: (x) => `${clientName(x)} ${x.clientId}`,
    meta: { mobile: "title" },
    cell: ({ row }) =>
      h("div", { class: "min-w-0 whitespace-normal sm:max-w-[18rem]" }, [
        h("p", { class: "font-medium [overflow-wrap:anywhere]" }, clientName(row.original)),
        h("p", { class: "truncate font-mono text-xs font-normal text-muted-foreground", title: row.original.clientId }, row.original.clientId),
      ]),
  },
  {
    id: "kind",
    header: c.columns.kind,
    accessorFn: (x) => oauth.kind[x.kind],
    meta: { mobile: "subtitle" },
    cell: ({ row }) => h("span", { class: "text-sm whitespace-normal" }, oauth.kind[row.original.kind]),
  },
  { id: "status", header: c.columns.status, accessorFn: (x) => x.status, meta: { mobile: "badge" }, cell: ({ row }) => statusBadge(row.original.status) },
  {
    id: "redirects",
    header: c.columns.redirects,
    enableSorting: false,
    accessorFn: (x) => x.redirectUris.map((r) => r.label).join(" "),
    meta: { mobile: "meta" },
    cell: ({ row }) =>
      h(
        "ul",
        { class: "space-y-0.5 text-sm" },
        row.original.redirectUris.map((r) =>
          h("li", { class: "flex min-w-0 flex-wrap items-center gap-1.5", title: r.uri }, [h("span", { class: "min-w-0 [overflow-wrap:anywhere]" }, r.label), r.needsApproval ? h(Badge, { variant: "warning" }, () => c.needsApproval) : null]),
        ),
      ),
  },
  {
    id: "usage",
    header: c.columns.usage,
    accessorFn: (x) => x.people,
    meta: { mobile: "meta" },
    cell: ({ row }) =>
      h("div", { class: "text-sm whitespace-nowrap text-muted-foreground" }, [h("div", c.people(row.original.people)), h("div", c.grants(row.original.activeGrants))]),
  },
  {
    id: "lastUsed",
    header: c.columns.lastUsed,
    accessorFn: (x) => x.lastUsedAt ?? x.createdAt,
    meta: { mobile: "meta" },
    cell: ({ row }) => h(RelativeTime, { value: row.original.lastUsedAt ?? row.original.createdAt, class: "text-sm whitespace-nowrap" }),
  },
]);
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="c.title" :description="c.description" />
    <FilterBar label="Filter clients" class="mb-5">
      <template #chips><FilterChips v-model="status" :options="filterOptions" label="Status" /></template>
    </FilterBar>
    <ListSkeleton v-if="clients.isPending.value" :rows="5" />
    <ErrorState v-else-if="clients.error.value" :error="clients.error.value" @retry="clients.refetch()" />
    <EmptyState v-else-if="!clients.data.value?.length" :icon="Blocks" :title="c.empty" :description="c.emptyHint" />
    <DataTable v-else :data="clients.data.value" :columns="columns" row-clickable :row-key="(x) => x.uid" :caption="c.title" @row-click="show" />

    <Sheet v-model:open="open">
      <SheetContent side="right" class="w-full overflow-y-auto sm:max-w-md">
        <template v-if="selected">
          <SheetHeader>
            <SheetTitle>{{ clientName(selected) }}</SheetTitle>
            <SheetDescription>{{ oauth.kind[selected.kind] }}</SheetDescription>
          </SheetHeader>
          <div class="flex flex-col gap-5 px-4 pb-6">
            <div class="flex flex-wrap items-center gap-2">
              <Badge :variant="STATUS_VARIANT[selected.status]">{{ oauth.status[selected.status] }}</Badge>
              <span class="text-sm text-muted-foreground">{{ c.people(selected.people) }} · {{ c.grants(selected.activeGrants) }}</span>
            </div>
            <section>
              <p class="eyebrow mb-1.5">{{ c.clientId }}</p>
              <p class="font-mono text-xs break-all">{{ selected.clientId }}</p>
            </section>
            <section>
              <p class="eyebrow mb-1.5">{{ c.columns.redirects }}</p>
              <ul class="space-y-1 text-sm">
                <li v-for="r in selected.redirectUris" :key="r.uri" class="flex flex-wrap items-center gap-1.5">
                  <span class="font-mono text-xs break-all">{{ r.uri }}</span>
                  <Badge v-if="r.needsApproval" variant="warning">{{ c.needsApproval }}</Badge>
                </li>
              </ul>
            </section>
            <section v-if="selected.registeredIp || selected.registeredUserAgent">
              <p class="eyebrow mb-1.5">{{ c.registeredFrom }}</p>
              <p class="text-sm break-all">{{ selected.registeredIp }}<template v-if="selected.registeredUserAgent"> · {{ selected.registeredUserAgent }}</template></p>
              <p class="text-xs text-muted-foreground"><RelativeTime :value="selected.createdAt" /></p>
            </section>
            <section v-if="selected.statusReason">
              <p class="eyebrow mb-1.5">{{ c.reason }}</p>
              <p class="text-sm">{{ selected.statusReason }}</p>
            </section>
            <div class="flex flex-col gap-2">
              <Button v-if="selected.status !== 'active'" :disabled="busy" @click="decide(selected, 'active')">
                <BadgeCheck /> {{ selected.status === "blocked" ? c.unblock : c.approve }}
              </Button>
              <Button v-if="selected.status !== 'blocked'" variant="destructive" :disabled="busy" @click="decide(selected, 'blocked')"><Ban /> {{ c.block }}…</Button>
              <p class="text-xs text-muted-foreground">{{ t.admin.mcpClientsAudited }}</p>
            </div>
          </div>
        </template>
      </SheetContent>
    </Sheet>
  </div>
</template>
